import { FormEvent, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useStore } from "../store";

type Mode = "login" | "register";

interface LocationState {
  from?: { pathname: string };
  mode?: Mode;
}

export default function Login() {
  const currentUser = useStore((s) => s.currentUser);
  const authChecked = useStore((s) => s.authChecked);
  const error = useStore((s) => s.error);
  const loading = useStore((s) => s.loading);
  const login = useStore((s) => s.login);
  const register = useStore((s) => s.register);
  const clearError = useStore((s) => s.clearError);

  const location = useLocation();
  const state = location.state as LocationState | undefined;
  const fromPath = state?.from?.pathname;
  // After auth, send the user to the page they were trying to reach,
  // falling back to /dashboard. We never bounce back to / (landing) or
  // /login itself.
  const redirectTo =
    fromPath && fromPath !== "/login" && fromPath !== "/" ? fromPath : "/dashboard";

  // Initial mode can come from <Link state={{ mode: 'register' }} /> on the
  // landing page so the "Create account" CTA opens the form in register mode.
  const [mode, setMode] = useState<Mode>(state?.mode ?? "login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [fullName, setFullName] = useState("");

  // If we're already authenticated, redirect away.
  if (authChecked && currentUser) {
    return <Navigate to={redirectTo} replace />;
  }

  async function handleSubmit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    try {
      if (mode === "login") {
        await login(email.trim(), password);
      } else {
        await register(email.trim(), password, fullName.trim() || undefined);
      }
      // On success the store updates currentUser; the redirect above will fire.
    } catch {
      // Error message is already in the store; no further handling needed.
    }
  }

  function switchMode(next: Mode) {
    clearError();
    setMode(next);
  }

  return (
    <div className="auth-shell">
      <div className="auth-card">
        <div className="auth-logo">
          Robot<span>wins</span>
        </div>
        <h1 className="auth-title">
          {mode === "login" ? "Welcome back" : "Create your account"}
        </h1>
        <p className="auth-subtitle">
          {mode === "login"
            ? "Sign in to continue to your projects."
            : "Closed beta — your email must be on the allowlist."}
        </p>

        <form className="auth-form" onSubmit={handleSubmit}>
          {mode === "register" && (
            <label className="auth-field">
              <span>Full name</span>
              <input
                type="text"
                autoComplete="name"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Jane Doe"
              />
            </label>
          )}

          <label className="auth-field">
            <span>Email</span>
            <input
              type="email"
              autoComplete="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
            />
          </label>

          <label className="auth-field">
            <span>Password</span>
            <input
              type="password"
              autoComplete={
                mode === "login" ? "current-password" : "new-password"
              }
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
            />
          </label>

          {error && <div className="auth-error">{error}</div>}

          <button
            type="submit"
            className="auth-submit"
            disabled={loading || !email || !password}
          >
            {loading
              ? "Working…"
              : mode === "login"
                ? "Sign in"
                : "Create account"}
          </button>
        </form>

        <div className="auth-toggle">
          {mode === "login" ? (
            <>
              Don't have an account?{" "}
              <button
                type="button"
                className="auth-link"
                onClick={() => switchMode("register")}
              >
                Create one
              </button>
            </>
          ) : (
            <>
              Already have an account?{" "}
              <button
                type="button"
                className="auth-link"
                onClick={() => switchMode("login")}
              >
                Sign in
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
